import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';
import { useAuthStore } from '../../store/authStore';
import { useFavoritesStore } from '../../store/favoritesStore';
import { HeartIcon } from '../ui/HeartIcon';

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg className="w-5 h-5 text-text-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {open ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 18L18 6M6 6l12 12" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h16" />
      )}
    </svg>
  );
}

function SearchIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
    </svg>
  );
}

function HeaderSearch() {
  const [value, setValue] = useState('');
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    navigate(trimmed ? `/catalogo?q=${encodeURIComponent(trimmed)}` : '/catalogo');
  };

  return (
    <form onSubmit={handleSubmit} className="hidden md:flex flex-1 max-w-xs mx-2">
      <div className="relative w-full">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none" />
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="O que você está buscando?"
          className="w-full pl-9 pr-9 py-2 rounded-full border border-champagne-dark bg-champagne-light text-text-dark placeholder-text-light focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
        />
        <button
          type="submit"
          aria-label="Buscar"
          className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full text-text-light hover:text-chocolate hover:bg-white/60 transition-colors"
        >
          <SearchIcon className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}

function NavLink({
  to,
  active,
  label,
  mobile,
  onClick,
}: {
  to: string;
  active: boolean;
  label: string;
  mobile?: boolean;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={[
        'flex items-center gap-2 rounded-full text-sm font-semibold transition-all duration-200',
        mobile ? 'px-4 py-2.5 w-full' : 'px-4 py-2',
        active
          ? 'bg-chocolate text-white shadow-sm'
          : 'text-text-medium border border-champagne-dark hover:border-chocolate hover:text-chocolate hover:bg-champagne-light',
      ].join(' ')}
    >
      {label}
    </Link>
  );
}

function CartIcon({ count }: { count: number }) {
  return (
    <Link to="/cart" aria-label="Carrinho" className="group relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 shrink-0">
      <span className="relative flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-champagne-light group-hover:bg-champagne transition-colors">
        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-text-dark group-hover:text-chocolate transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.4 7h12.8M7 13L5.4 5M17 21a1 1 0 11-2 0 1 1 0 012 0zm-8 0a1 1 0 11-2 0 1 1 0 012 0z" />
        </svg>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-chocolate text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </span>
    </Link>
  );
}

function FavoritesIcon({ count }: { count: number }) {
  return (
    <Link to="/favoritos" aria-label="Favoritos" className="group relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 shrink-0">
      <span className="relative flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-champagne-light group-hover:bg-champagne transition-colors">
        <HeartIcon className="w-5 h-5 sm:w-6 sm:h-6 text-text-dark group-hover:text-chocolate transition-colors" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-chocolate text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </span>
    </Link>
  );
}

function UserMenu({ customerName, isAdmin, onLogout }: { customerName: string | null; isAdmin: boolean; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Minha conta"
        className="group relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 shrink-0"
      >
        <span className={`flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full transition-colors ${
          open ? 'bg-champagne' : 'bg-champagne-light group-hover:bg-champagne'
        }`}>
          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-text-dark group-hover:text-chocolate transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-champagne py-1.5 z-50">
          {customerName && (
            <div className="px-4 pb-2 mb-1 border-b border-champagne">
              <p className="text-sm font-semibold text-text-dark truncate">{customerName}</p>
            </div>
          )}
          <Link
            to="/perfil"
            onClick={() => setOpen(false)}
            className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
              pathname === '/perfil' ? 'text-chocolate bg-champagne-light' : 'text-text-dark hover:bg-champagne-light'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Meu Perfil
          </Link>

          <Link
            to="/perfil?tab=pedidos"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-text-dark hover:bg-champagne-light transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Meus Pedidos
          </Link>

          {isAdmin && (
            <Link
              to="/configuracoes"
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                pathname === '/configuracoes' ? 'text-chocolate bg-champagne-light' : 'text-text-dark hover:bg-champagne-light'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Configurações
            </Link>
          )}

          <div className="border-t border-champagne my-1.5" />

          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors w-full"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

export function Header() {
  const totalItems = useCartStore((s) => s.totalItems());
  const totalFavorites = useFavoritesStore((s) => s.favoriteIds.length);
  const { isAuthenticated, customerName, isAdmin, logout } = useAuthStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <header className="bg-white border-b border-champagne sticky top-0 z-50">
      {/* Corpo do header */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 h-16 flex items-center justify-between gap-2 sm:gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 sm:gap-2.5 group shrink-0 min-w-0">
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-display text-lg sm:text-xl font-semibold text-text-dark tracking-tight truncate">
              baby<em className="italic font-medium text-chocolate group-hover:text-chocolate-dark transition-colors">chic</em>
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-4">
          <NavLink to="/" active={pathname === '/'} label="Início" />
          <NavLink to="/catalogo" active={pathname.startsWith('/catalogo')} label="Catálogo" />
          <NavLink to="/sobre" active={pathname === '/sobre'} label="Sobre" />
        </nav>

        <HeaderSearch />

        <button
          type="button"
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label="Abrir menu de navegação"
          aria-expanded={mobileMenuOpen}
          className={`md:hidden flex items-center justify-center w-9 h-9 rounded-full transition-colors shrink-0 ${
            mobileMenuOpen ? 'bg-champagne' : 'hover:bg-champagne'
          }`}
        >
          <MenuIcon open={mobileMenuOpen} />
        </button>

        {/* Ações direita */}
        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          {isAuthenticated ? (
            <UserMenu customerName={customerName} isAdmin={isAdmin} onLogout={handleLogout} />
          ) : (
            <>
              <span className="hidden lg:block text-sm text-text-medium mr-1">Olá, bem-vindo(a)!</span>
              <Link
                to="/cadastro"
                className="hidden sm:block text-sm font-medium text-text-medium border border-champagne-dark hover:border-chocolate hover:text-chocolate px-4 py-2 rounded-full transition-colors"
              >
                Cadastre-se
              </Link>
              <Link
                to="/login"
                className="text-sm font-medium text-white bg-chocolate hover:bg-chocolate-dark px-3 py-1.5 sm:px-5 sm:py-2 rounded-full transition-colors whitespace-nowrap"
              >
                Entrar
              </Link>
            </>
          )}

          <FavoritesIcon count={totalFavorites} />

          <CartIcon count={totalItems} />
        </div>
      </div>

      {mobileMenuOpen && (
        <nav className="md:hidden border-t border-champagne bg-white px-3 py-3 flex flex-col gap-2">
          <NavLink to="/" active={pathname === '/'} label="Início" mobile />
          <NavLink to="/catalogo" active={pathname.startsWith('/catalogo')} label="Catálogo" mobile />
          <NavLink to="/sobre" active={pathname === '/sobre'} label="Sobre" mobile />
        </nav>
      )}
    </header>
  );
}
